import { ColumnDef } from "@tanstack/react-table";
import IconButton from "@/components/IconButton";
import Badge from "@/components/Badge/Badge";
import { MaterialNames } from "@/components/material_symbol_names";
import type { Action } from "@/components/MenuButton";
import { MenuItem } from "@/hooks/useActionMenu";
import classNames from "classnames";

type SetMenuFn = (menu: { rowId: string; items: MenuItem[] }) => void;
type SetActionsFn = (menu: { rowId: string; actions: Action[] }) => void;

export interface BuildActionColumnProps<T> {
  /**
   * @deprecated Prefer `getActions` + `useTableRowMenu` for new tables. The
   * `MenuItem` shape will be removed once existing callers are migrated.
   */
  getMenuItems?: (row: T) => MenuItem[];
  /** Higher-level shape powered by `useTableRowMenu`. */
  getActions?: (row: T) => Action[];
  setMenu: SetMenuFn | SetActionsFn;
  onViewClick?: (row: T, event: React.MouseEvent) => void;
  showViewButton?: (row: T) => boolean;
  onEditClick?: (row: T, event: React.MouseEvent) => void;
  editButtonTitle?: string;
  isRowNavigable?: (row: T) => boolean;
  viewButtonTitle?: string;
  viewButtonIcon?: MaterialNames;
  actionId: (rowId: string) => string;
  getRowId?: (row: T) => string;
  getBadgeCount?: (row: T) => number;
}

export function buildActionColumn<T extends { id: string }>({
  getMenuItems,
  getActions,
  setMenu,
  onViewClick,
  showViewButton,
  onEditClick,
  editButtonTitle = "Edit",
  isRowNavigable,
  viewButtonTitle = "View Details",
  viewButtonIcon = "open_in_full",
  actionId,
  getRowId = (row) => row.id,
  getBadgeCount
}: BuildActionColumnProps<T>): ColumnDef<T, any> {
  if (process.env.NODE_ENV !== "production" && getMenuItems && getActions) {
    throw new Error("buildActionColumn: pass either `getMenuItems` (legacy) or `getActions` (new), not both.");
  }

  return {
    id: "actions",
    header: () => null,
    enableSorting: false,
    size: 100,
    cell: (cell) => {
      const { row } = cell;
      const isCompactLayout = cell.table.options.meta?.isCompactLayout;
      const rowId = getRowId(row.original);
      const useAltBorder = cell.table.options.meta?.useAltBorder;

      const items = getActions ? [] : (getMenuItems?.(row.original) ?? []);
      const actions = getActions ? getActions(row.original) : [];
      const hasMenuItems = getActions ? actions.length > 0 : items.length > 0;

      function onMenuClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (getActions) {
          (setMenu as SetActionsFn)({ rowId, actions });
        } else {
          (setMenu as SetMenuFn)({ rowId, items });
        }
      }

      function onExpandClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        e.stopPropagation();
        onViewClick?.(row.original, e);
      }

      function handleEditClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        e.stopPropagation();
        onEditClick?.(row.original, e);
      }

      const badgeCount = getBadgeCount?.(row.original) ?? 0;
      const shouldShowViewButton = onViewClick && (!showViewButton || showViewButton(row.original));

      let shouldShowNavigateIcon = false;
      if (!hasMenuItems && !onEditClick) {
        if (isRowNavigable !== undefined) {
          shouldShowNavigateIcon = isRowNavigable(row.original);
        } else if (cell.table.options.meta?.onClickRow && !shouldShowViewButton) {
          shouldShowNavigateIcon = true;
        }
      }

      const editButton = onEditClick && (
        <IconButton
          onClick={handleEditClick}
          data-testid="edit-action"
          title={editButtonTitle}
          name="edit"
          className="!m-0 !p-0 h-10"
        />
      );

      const viewButton = shouldShowViewButton && (
        <div className="relative">
          <IconButton
            data-testid="expand-details"
            title={viewButtonTitle}
            name={viewButtonIcon}
            iconClassName="text-sys-surface-variant"
            className="!m-0 !p-0"
            onClick={onExpandClick}
          />
          {badgeCount > 0 && (
            <Badge value={badgeCount} className="absolute top-0 -right-1 pointer-events-none" testId="action-badge" />
          )}
        </div>
      );

      const menuButton = hasMenuItems && (
        <IconButton
          id={actionId(rowId)}
          onClick={onMenuClick}
          data-testid="actions"
          title="actions"
          name="more_vert"
          iconClassName="font-extrabold"
          className="!m-0 !p-0 h-10"
        />
      );

      return (
        <div
          className={classNames(isCompactLayout ? "flex flex-col items-center" : "flex items-center justify-end", {
            "pr-4": useAltBorder && isCompactLayout
          })}
        >
          {isCompactLayout ? (
            <>
              {editButton}
              {menuButton}
              {shouldShowNavigateIcon && (
                <IconButton name="chevron_right" className="!m-0 !p-0 h-10" data-testid="mobile-open-action-button" />
              )}
              {viewButton}
            </>
          ) : (
            <>
              {viewButton}
              {editButton}
              {menuButton}
            </>
          )}
        </div>
      );
    },
    meta: {
      skipEmptyState: true
    }
  };
}
